import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { dateComplete } from './dates';
import { messageDErreur, useSession } from '../auth/session';
import { useTraduction } from '../i18n/langue';
import { Alerte, Bouton, Carte } from '../ui';
import type { SuppressionDossier } from './types';

/**
 * Le bandeau d'état d'une suppression en cours.
 *
 * Il est posé **en haut du dossier, dans toutes les catégories**, et non dans une carte au
 * fond d'un onglet : une suppression demandée est un état de l'écran entier. Un parent qui
 * ouvre sa messagerie doit savoir que le dossier qu'il lit est en train d'être détruit,
 * même s'il ne descend jamais jusqu'au bas d'« Aujourd'hui ».
 *
 * Trois textes pour trois situations, jamais mélangés : la demande est partie et on attend
 * l'autre ; la demande est arrivée et c'est à moi de répondre ; c'est validé et le compte à
 * rebours court. Dire « ce dossier sera supprimé » dans le premier cas serait faux — il ne
 * le sera que si l'autre parent le veut aussi.
 *
 * Le bouton d'annulation est présent dans les trois, sans condition. C'est la règle du
 * dispositif : il faut être deux pour détruire, il suffit d'être un pour arrêter.
 */
export function BandeauSuppression({
  caseFileId,
  suppression,
}: {
  caseFileId: string;
  suppression: SuppressionDossier;
}) {
  const { client } = useSession();
  const { messages } = useTraduction();
  const m = messages.dossier.suppression;
  const queryClient = useQueryClient();

  const annuler = useMutation({
    mutationFn: () => client.request(`/case-files/${caseFileId}/deletion`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['case-file', caseFileId] });
      void queryClient.invalidateQueries({ queryKey: ['case-files'] });
    },
  });

  const archive = suppression.status === 'archived';
  const purge = suppression.purgeAt ? new Date(suppression.purgeAt) : null;

  return (
    <div className={`suppression-bandeau${archive ? ' archive' : ''}`} role="status">
      <p className="suppression-titre">{archive ? m.archiveTitre : m.titre}</p>

      {archive ? (
        <>
          <p>{purge ? m.archiveTexte(dateComplete(purge)) : m.archiveTitre}</p>
          <p className="discret">{m.archiveLecture}</p>
        </>
      ) : (
        <>
          <p>{suppression.awaitingViewer ? m.attenteVous : m.attenteAutre}</p>
          <p className="discret">{m.attenteEcheance(dateComplete(new Date(suppression.expiresAt)))}</p>
        </>
      )}

      {annuler.isError ? (
        <Alerte titre={m.erreur}>{messageDErreur(annuler.error)}</Alerte>
      ) : null}

      <Bouton onClick={() => annuler.mutate()} disabled={annuler.isPending}>
        {annuler.isPending ? m.annulationEnCours : m.annuler}
      </Bouton>
    </div>
  );
}

/**
 * La demande de suppression, en bas de l'écran et derrière un lien.
 *
 * Elle est **la dernière chose** de la catégorie « Aujourd'hui », et repliée. Un bouton
 * « Supprimer » visible en permanence sur l'écran qu'on ouvre chaque matin pour savoir chez
 * qui dort l'enfant serait une cible offerte à des années de clics distraits. Le lien
 * l'annonce sans l'exposer ; le texte qu'il déroule dit tout le parcours avant de proposer
 * quoi que ce soit.
 *
 * Le composant disparaît dès qu'une suppression est en cours : c'est alors le bandeau qui
 * parle, et laisser un second point d'entrée ouvert n'aurait rien fait de plus.
 */
export function SupprimerDossier({
  caseFileId,
  modeSolo,
}: {
  caseFileId: string;
  modeSolo: boolean;
}) {
  const { client } = useSession();
  const { messages } = useTraduction();
  const m = messages.dossier.suppression;
  const queryClient = useQueryClient();
  const [ouvert, setOuvert] = useState(false);

  const demander = useMutation({
    mutationFn: () => client.request(`/case-files/${caseFileId}/deletion`, { method: 'POST' }),
    onSuccess: () => {
      setOuvert(false);
      void queryClient.invalidateQueries({ queryKey: ['case-file', caseFileId] });
      void queryClient.invalidateQueries({ queryKey: ['case-files'] });
    },
  });

  if (!ouvert) {
    return (
      <p className="suppression-appel">
        <Bouton variante="lien" onClick={() => setOuvert(true)}>
          {m.ouvrir}
        </Bouton>
      </p>
    );
  }

  return (
    <Carte titre={m.titre}>
      <p style={{ marginTop: 0 }}>{modeSolo ? m.texteSolo : m.texte}</p>

      {demander.isError ? (
        <Alerte titre={m.erreur}>{messageDErreur(demander.error)}</Alerte>
      ) : null}

      <Bouton onClick={() => demander.mutate()} disabled={demander.isPending}>
        {demander.isPending ? m.envoiEnCours : m.demander}
      </Bouton>{' '}
      <Bouton variante="secondaire" onClick={() => setOuvert(false)}>
        {m.renoncer}
      </Bouton>
    </Carte>
  );
}
